import type { Habit, HabitLog } from '@/types/types';

/** Format a Date as YYYY-MM-DD in local time. */
export function getLocalDateString(date: Date = new Date()): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/** Normalise legacy problem values that may be a raw string or JSON-encoded array. */
export function normalizeProblems(problems: string | string[] | null | undefined): string[] {
    if (Array.isArray(problems)) return problems;
    if (typeof problems === 'string' && problems.trim()) {
        try {
            const parsed = JSON.parse(problems);
            if (Array.isArray(parsed)) return parsed;
        } catch {}
        return [problems];
    }
    return [];
}

/** Calculate daily habit completion score (0–100) weighted by habit weight. */
export function calculateDailyScore(habits: Habit[], logs: HabitLog[]): number {
    if (habits.length === 0) return 0;

    let totalWeight = 0;
    let completedWeight = 0;

    habits.forEach(habit => {
        totalWeight += habit.weight;
        const isDone = logs.some(l => l.habit_id === habit.id && l.status);
        if (isDone) completedWeight += habit.weight;
    });

    if (totalWeight === 0) return 0;
    return Math.round((completedWeight / totalWeight) * 100);
}

// Concavity of the consistency → progress curve. Higher K = more front-loaded
// (easier to reach mid-progress, harder to approach 100%). Tunable knob.
export const CONSISTENCY_ASYMPTOTE_K = 1.5;

/**
 * Consistency progress from a completed/elapsed ratio (0-1). No streak: the
 * ratio alone drives an asymptotic transform so early progress comes easily and
 * the last stretch to 100% is continuously harder (owner direction 2026-07-23).
 * Normalised so ratio 1 → 100 and ratio 0 → 0.
 *   progress = 100 · (1 − e^(−K·ratio)) / (1 − e^(−K))
 */
export function calculateConsistencyScore(ratio: number): number {
    const r = Math.max(0, Math.min(1, ratio));
    const k = CONSISTENCY_ASYMPTOTE_K;
    return Math.round((100 * (1 - Math.exp(-k * r))) / (1 - Math.exp(-k)));
}

/** @internal Calculate current streak, max streak, and consistency for a single habit. */
export function calculateHabitStreaks(
    habitId: string,
    allLogs: HabitLog[],
): { currentStreak: number; maxStreak: number; consistency: number } {
    const logs = allLogs.filter(l => l.habit_id === habitId && l.status);
    const dates = Array.from(new Set(logs.map(l => l.date))).sort();
    const dateSet = new Set(dates);

    if (dates.length === 0) return { currentStreak: 0, maxStreak: 0, consistency: 0 };

    const firstDate = new Date(dates[0]);
    const today = new Date();
    const totalDays = Math.max(1, Math.round((today.getTime() - firstDate.getTime()) / (1000 * 3600 * 24)) + 1);
    const consistency = logs.length / totalDays;

    let max = 0;
    let current = 0;
    let prevTimestamp: number | null = null;

    for (const dStr of dates) {
        const timestamp = new Date(dStr).getTime();
        if (prevTimestamp) {
            const diffDays = Math.round((timestamp - prevTimestamp) / (1000 * 3600 * 24));
            if (diffDays === 1) {
                current++;
            } else {
                max = Math.max(max, current);
                current = 1;
            }
        } else {
            current = 1;
        }
        prevTimestamp = timestamp;
    }
    max = Math.max(max, current);

    let streak = 0;
    const todayStr = today.toISOString().split('T')[0];
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];

    let checkDate: Date | null = null;
    if (dateSet.has(todayStr)) {
        streak = 1;
        checkDate = new Date(today);
    } else if (dateSet.has(yesterdayStr)) {
        streak = 1;
        checkDate = new Date(yesterday);
    }

    if (checkDate) {
        while (true) {
            checkDate.setDate(checkDate.getDate() - 1);
            const checkStr = checkDate.toISOString().split('T')[0];
            if (dateSet.has(checkStr)) {
                streak++;
            } else {
                break;
            }
        }
    }

    return { currentStreak: streak, maxStreak: max, consistency };
}
